import moment from 'moment';
import EventTypes from '../types';
import {
  accessToOptions,
  filterGroupsLevels,
  filterTiers,
} from '../accessFields';
import { isValidUrl } from '../../../util/functions';
import { partial } from '../../../util/translation/translation';
import {
  paymentFormValues,
  paymentPayload,
  seriesPricePayload,
  validatePaymentValues,
} from '../payments';

// Moving between a series (API) and the series form (redux-form values).
// The form is split in two sections that the series page edits on separate
// tabs: 'details' (shared event fields) and 'schedule' (schedule and
// booking rules). Each section's payload only has its own fields.

export const SERIES_SECTIONS = ['details', 'schedule'];

const NOT_APPLICABLE = { label: 'N/A', value: null };

// adminEmail: the admin creating it, told when a place frees up
export const newSeriesValues = (noCategoryOption, { adminEmail } = {}) => ({
  targetAudience: EventTypes.TargetAudience.partner,
  eventCategories: noCategoryOption,
  eventLevels: [NOT_APPLICABLE],
  eventGroups: [NOT_APPLICABLE],
  eventTiers: [NOT_APPLICABLE],
  feedVisibility: 'ALL',
  participantsVisible: true,
  waitlistEnabled: false,
  bindingRegistration: false,
  bookingMode: 'occurrence',
  scheduleKind: 'weekly',
  weekdays: [],
  times: ['18:00'],
  intervalFrom: '10:00',
  intervalTo: '16:00',
  everyMinutes: 30,
  durationMinutes: 60,
  startDate: moment().format('YYYY-MM-DD'),
  endDate: '',
  weeksAhead: 8,
  skipDates: [],
  registrationOpensDaysBefore: '',
  registrationClosesHoursBefore: 0,
  maxGuests: 0,
  ...paymentFormValues(null, { adminEmail }),
  seriesPrice: '',
});

export const seriesToFormValues = (
  series,
  { noCategoryOption, accessOptions, allLabels, countryOptions }
) => {
  const { schedule = {} } = series;
  return {
    title: series.title,
    description: series.description,
    imageUrl: series.imageUrl ? { preview: series.imageUrl } : null,
    targetAudience: series.targetAudience,
    eventCategories: series.category
      ? {
          label: series.category.title,
          value: series.category.id,
          maximum: series.category.maxAttendees,
        }
      : noCategoryOption,
    maxAttendees: series.maxAttendees,
    eventLevels: accessToOptions(series.accessLevels, accessOptions.levels, {
      notApplicable: NOT_APPLICABLE,
      allAllowed: allLabels.eventLevels,
    }),
    eventGroups: accessToOptions(
      series.accessCategories,
      accessOptions.groups,
      { notApplicable: NOT_APPLICABLE, allAllowed: allLabels.eventGroups }
    ),
    eventTiers: accessToOptions(series.accessTiers, accessOptions.tiers, {
      notApplicable: NOT_APPLICABLE,
      allAllowed: allLabels.eventTiers,
      byValue: true,
    }),
    feedVisibility: series.feedVisibility ? 'ALL' : 'ONLYINVITED',
    participantsVisible: series.participantsVisible !== false,
    address: series.address,
    zip: series.zip,
    city: series.city,
    country: countryOptions.find((option) => option.name === series.country),
    websiteUrl: series.websiteUrl,
    urlText: series.urlText,
    mapsUrl: series.mapsUrl,
    waitlistEnabled: !!series.waitlistEnabled,
    bindingRegistration: !!series.bindingRegistration,
    bookingMode: series.bookingMode || 'occurrence',
    scheduleKind: schedule.kind || 'weekly',
    weekdays: schedule.weekdays || [],
    times: schedule.times?.length ? schedule.times : ['18:00'],
    intervalFrom: schedule.from || '10:00',
    intervalTo: schedule.to || '16:00',
    everyMinutes: schedule.everyMinutes || 30,
    durationMinutes: schedule.durationMinutes,
    startDate: schedule.startDate,
    endDate: schedule.endDate || '',
    weeksAhead: series.weeksAhead,
    skipDates: series.skipDates || [],
    registrationOpensDaysBefore:
      series.registrationOpensDaysBefore === null ||
      series.registrationOpensDaysBefore === undefined
        ? ''
        : series.registrationOpensDaysBefore,
    registrationClosesHoursBefore: series.registrationClosesHoursBefore || 0,
    // Places per booking include the booker
    maxGuests: Math.max(0, (series.maxAccountAttendees || 1) - 1),
    // Price per place (and for the whole series), fee, VAT and waitlist
    ...paymentFormValues(series),
  };
};

// The schedule as the API wants it
export const toSchedule = (values) => ({
  kind: values.scheduleKind,
  weekdays: [...values.weekdays].sort(),
  ...(values.scheduleKind === 'interval'
    ? {
        from: values.intervalFrom,
        to: values.intervalTo,
        everyMinutes: Number(values.everyMinutes),
      }
    : { times: [...new Set(values.times.filter(Boolean))].sort() }),
  durationMinutes: Number(values.durationMinutes),
  startDate: values.startDate,
  endDate: values.endDate || null,
});

const hasCategory = (values) => !!values.eventCategories?.value;

const maxAccountAttendees = (values) => Number(values.maxGuests || 0) + 1;

// isNew: a new series (CreateSeries) rather than an update, where empty
// optional fields are left out instead of cleared
export const detailsPayload = (values, isNew) => {
  const textOrNull = (value) => {
    const text = typeof value === 'string' ? value.trim() : value;
    if (text) return text;
    return isNew ? undefined : null;
  };
  return {
    title: values.title,
    description: values.description,
    address: textOrNull(values.address),
    zip: textOrNull(values.zip),
    city: textOrNull(values.city),
    country: textOrNull(values.country?.name),
    websiteUrl: isValidUrl(values.websiteUrl)
      ? values.websiteUrl
      : textOrNull(null),
    urlText: textOrNull(values.urlText),
    mapsUrl: textOrNull(values.mapsUrl),
    // A category sets the places per occurrence and per booking
    ...(hasCategory(values)
      ? { categoryId: values.eventCategories.value }
      : {
          ...(!isNew && { categoryId: null }),
          maxAttendees: Number(values.maxAttendees),
          maxAccountAttendees: maxAccountAttendees(values),
        }),
    accessLevels: filterGroupsLevels(values.eventLevels, values.targetAudience),
    accessCategories: filterGroupsLevels(
      values.eventGroups,
      values.targetAudience
    ),
    accessTiers: filterTiers(values.eventTiers, values.targetAudience),
    targetAudience: values.targetAudience,
    feedVisibility: values.feedVisibility === 'ALL',
    participantsVisible: !!values.participantsVisible,
  };
};

export const schedulePayload = (values) => ({
  schedule: toSchedule(values),
  skipDates: values.skipDates,
  weeksAhead: Number(values.weeksAhead),
  registrationOpensDaysBefore:
    values.registrationOpensDaysBefore === '' ||
    values.registrationOpensDaysBefore === null ||
    values.registrationOpensDaysBefore === undefined
      ? null
      : Number(values.registrationOpensDaysBefore),
  registrationClosesHoursBefore: Number(
    values.registrationClosesHoursBefore || 0
  ),
  bookingMode: values.bookingMode,
  waitlistEnabled: !!values.waitlistEnabled,
  bindingRegistration: !!values.bindingRegistration,
  // Copied to the occurrences like the other fields
  ...paymentPayload(values),
  ...seriesPricePayload(values),
});

// Leaves out undefined fields (JSON would too, but this keeps logs tidy)
export const compact = (payload) =>
  Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined)
  );

// Who can book: partners need a level or group, fans a tier (as on events)
export const accessIsMissing = (values) => {
  const { TargetAudience } = EventTypes;
  if (values.targetAudience === TargetAudience.partner) {
    return (
      !filterGroupsLevels(values.eventLevels, values.targetAudience) &&
      !filterGroupsLevels(values.eventGroups, values.targetAudience)
    );
  }
  if (values.targetAudience === TargetAudience.private) {
    return !filterTiers(values.eventTiers, values.targetAudience);
  }
  return false;
};

const isWholeNumber = (value, min, max) => {
  if (value === '' || value === null || value === undefined) return false;
  const number = Number(value);
  return Number.isInteger(number) && number >= min && number <= max;
};

export const validateSeries = (values, { sections = SERIES_SECTIONS }) => {
  const v = partial('validateErrors');
  const p = partial('EventSeries');
  const errors = {};
  const noCategory = !hasCategory(values);

  if (sections.includes('details')) {
    if (!values.title) errors.title = v('nameFieldEmpty');
    if (!values.description) {
      errors.description = v('descriptionFieldCantBeEmpty');
    }
    if (noCategory && !isWholeNumber(values.maxAttendees, 1, 100000)) {
      errors.maxAttendees = v('participantsFieldCantBeEmpty');
    }
    if (noCategory) {
      if (!isWholeNumber(values.maxGuests, 0, 1000)) {
        errors.maxGuests = p('errorWholeNumber');
      } else if (
        values.maxAttendees &&
        Number(values.maxGuests) + 1 > Number(values.maxAttendees)
      ) {
        errors.maxGuests = p('errorGuestsOverPlaces');
      }
    }
    if (values.mapsUrl && !isValidUrl(values.mapsUrl)) {
      errors.mapsUrl = v('mapsUrlFieldCantBeEmpty');
    }
    if (values.websiteUrl && !isValidUrl(values.websiteUrl)) {
      errors.websiteUrl = v('websiteUrlFieldCantBeEmpty');
    }
  }

  if (sections.includes('schedule')) {
    if (!values.weekdays?.length) errors.weekdays = p('errorNoDays');
    if (values.scheduleKind === 'interval') {
      if (!values.intervalFrom) errors.intervalFrom = p('errorTime');
      if (!values.intervalTo) errors.intervalTo = p('errorTime');
      if (
        values.intervalFrom &&
        values.intervalTo &&
        values.intervalTo < values.intervalFrom
      ) {
        errors.intervalTo = p('errorLastBeforeFirst');
      }
      if (!isWholeNumber(values.everyMinutes, 5, 720)) {
        errors.everyMinutes = p('errorEveryMinutes');
      }
    } else if (!values.times?.some(Boolean)) {
      errors.times = p('errorNoTimes');
    }
    if (!isWholeNumber(values.durationMinutes, 5, 1440)) {
      errors.durationMinutes = p('errorDuration');
    }
    if (!values.startDate) errors.startDate = v('dateFieldCantBeEmpty');
    if (
      values.endDate &&
      values.startDate &&
      values.endDate < values.startDate
    ) {
      errors.endDate = v('endDateEarlierThanStartDate');
    }
    if (!isWholeNumber(values.weeksAhead, 1, 52)) {
      errors.weeksAhead = p('errorWeeksAhead');
    }
    if (
      values.registrationOpensDaysBefore !== '' &&
      values.registrationOpensDaysBefore !== null &&
      values.registrationOpensDaysBefore !== undefined &&
      !isWholeNumber(values.registrationOpensDaysBefore, 0, 365)
    ) {
      errors.registrationOpensDaysBefore = p('errorWholeNumber');
    }
    if (!isWholeNumber(values.registrationClosesHoursBefore, 0, 8760)) {
      errors.registrationClosesHoursBefore = p('errorWholeNumber');
    }
    // Price, fee, the whole-series price (series.SeriesPriceRequired) and
    // the waitlist's time to pay
    validatePaymentValues(values, errors, { series: true });
  }
  return errors;
};
